import { createSlice, PayloadAction, createAsyncThunk, isPending, isRejected, isFulfilled, createSelector } from '@reduxjs/toolkit';
import { ICard } from '../models/ICard';
import { ICountry } from '../models/ICountry';
import { IUser } from '../models/IUser';
import { api } from '../api';
import preloadImagesTool from '../components/common/tools/PreloadImagesTool';
import { RootState } from './store';

export interface IdealCountryState {
  cards: ICard[];
  countries: ICountry[];
  shareResultCountries: ICountry[];
  user: IUser | null;
  currentCardIndex: number;
  currentCard: ICard | null;
  nextCard: ICard | null;
  removedCountryIds: number[];
  sessionToken: string;
  loading: boolean;
  error: string | null;
}

const initialState: IdealCountryState = {
  cards: [],
  countries: [],
  shareResultCountries: [],
  user: null,
  currentCardIndex: -1,
  currentCard: null,
  nextCard: null,
  removedCountryIds: [],
  sessionToken: '',
  loading: false,
  error: null,
};

// Async thunks
export const awaitWithLoading = createAsyncThunk(
  'idealCountry/awaitWithLoading',
  async (
    payload: { timeout: number } = { timeout: 0 },
    { rejectWithValue }
  ) => {
    try {
      return await new Promise(r => setTimeout(r, payload.timeout));
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const initializeTest = createAsyncThunk(
  'idealCountry/fetchInitialCard',
  async (
    payload: {} = {},
    { rejectWithValue }
  ) => {
    try {
      return await api.getNextCard({ ...payload });
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchResults = createAsyncThunk(
  'idealCountry/fetchResults',
  async (
    payload: { sessionId: string },
    { rejectWithValue }
  ) => {
    try {
      return await api.getResults({ ...payload });
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchNextCard = createAsyncThunk(
  'idealCountry/fetchNextCard',
  async (
    payload: {
      cardId?: number;
      value?: number;
      sessionId?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      return await api.getNextCard({ ...payload });
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchAllCards = createAsyncThunk(
  'idealCountry/fetchAllCards',
  async (_, { rejectWithValue }) => {
    try {
      return await api.getCards();
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

const idealCountrySlice = createSlice({
  name: 'idealCountry',
  initialState,
  reducers: {
    setSessionToken(state, action: PayloadAction<string>) {
      state.sessionToken = action.payload;
    },
    activateNextCard(state) {
      if (state.nextCard && state.nextCard.id !== state.currentCard?.id) {
        state.currentCardIndex += 1;
        state.currentCard = state.nextCard;
      }
    },
    removeCountry(state, action: PayloadAction<ICountry>) {
      state.removedCountryIds = [...state.removedCountryIds, action.payload.id];
    },
    resetQuestions(state) {
      state.currentCardIndex = -1;
      state.countries = [];
      state.nextCard = null;
      state.sessionToken = '';
      state.user = null;
      state.removedCountryIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Initial Card Fetching
      .addCase(initializeTest.fulfilled, (state, action) => {
        const { card, user } = action.payload;
        if (user) {
          state.user = user;
          state.sessionToken = user.token;
          state.nextCard = card || null;
        }
      })
      // fetchNextCard
      .addCase(fetchNextCard.fulfilled, (state, action) => {
        const { countries, card, user } = action.payload;
        // eslint-disable-next-line no-console
        console.log(user, card);
        state.countries = countries || [];
        state.nextCard = card || null;
        state.user = user || null;
        state.sessionToken = user?.token || '';
      })
      // fetchResults
      .addCase(fetchResults.fulfilled, (state, action) => {
        const { data: countries } = action.payload;
        state.shareResultCountries = (Array.isArray(countries) && countries) || [];
      })
      // fetchAllCards
      .addCase(fetchAllCards.fulfilled, (state, action) => {
        const cards = action.payload;
        state.cards = cards;
        preloadImagesTool.preloadImages(cards.map((x: ICard) => x.image_url));
      })
      .addMatcher(
        isPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        isRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload as string || action.error.message || 'Unknown error';
        }
      )
      .addMatcher(
        isFulfilled,
        (state) => {
          state.loading = false;
          state.error = null;
        }
      );
  },
});

export default idealCountrySlice;
export const {
  setSessionToken,
  activateNextCard,
  removeCountry,
  resetQuestions,
} = idealCountrySlice.actions;

// selectors
export const selectSectionNumber = createSelector(
  [(state: RootState) => state.idealCountry.currentCardIndex],
  (currentCardIndex) => Math.floor(currentCardIndex / 5)
);

export const selectTotalSectionsCount = createSelector(
  [],
  () => 10
);

export const selectCountries = createSelector(
  [
    (state: RootState) => state.idealCountry.countries,
    (state: RootState) => state.idealCountry.removedCountryIds
  ],
  (countries, removedCountryIds) =>
    countries
      .filter(x => !removedCountryIds.includes(x.id))
      .sort((a, b) => b.total_score - a.total_score)
);

export const selectShareResultCountries = createSelector(
  [(state: RootState) => state.idealCountry.shareResultCountries],
  (shareResultCountries) =>
    [...shareResultCountries].sort((a, b) => b.total_score - a.total_score)
);
